import { useCallback } from 'react';
import { useApiClient } from '@rudi/api/react';
import { TeamAccessEditor } from '@rudi/ui/team-access-editor';
import { useState } from 'react';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { toast } from 'sonner';
import { useTeamClinicalSetup } from '../api/teamClinicalSetup';
import type { TeamPracticeInput } from '../api/teamClinicalSetup';
import type { AdminTeam } from '../api/types';

const emptyPractice: TeamPracticeInput = {
  name: '',
  address: '',
  postalCode: '',
  city: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

const practiceFields: [keyof TeamPracticeInput, string][] = [
  ['name', 'Practice name'],
  ['address', 'Street address'],
  ['postalCode', 'Postal code'],
  ['city', 'City'],
  ['contactName', 'Contact name'],
  ['contactEmail', 'Contact email'],
  ['contactPhone', 'Contact phone'],
];

function detail(error: unknown): string {
  const value = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  return typeof value === 'string' ? value : 'Could not save. Please try again.';
}

export function TeamClinicalSetupPanel({
  tenantId,
  team,
  practicesOnly = false,
}: {
  tenantId: string;
  team: AdminTeam;
  practicesOnly?: boolean;
}) {
  const api = useApiClient();
  const request = useCallback(
    async (method: 'get' | 'post' | 'put' | 'patch' | 'delete', path: string, body?: unknown) => {
      const response =
        method === 'get' || method === 'delete'
          ? await api[method](path)
          : await api[method](path, body);
      return response.data;
    },
    [api]
  );
  const { setup, practices, createPractice } = useTeamClinicalSetup(tenantId, team.id);
  const [showPractice, setShowPractice] = useState(false);
  const [practice, setPractice] = useState(emptyPractice);
  const active = team.status === 'active';

  if (setup.isLoading) return <p className="text-sm">Loading clinical setup…</p>;
  if (setup.error) {
    const unavailable =
      (setup.error as { response?: { status?: number } }).response?.status === 404;
    return (
      <p role="alert" className="text-sm">
        {unavailable
          ? 'Clinical team setup is unavailable on this backend.'
          : 'Could not load clinical team setup.'}
        <Button variant="ghost" size="sm" onClick={() => setup.refetch()}>
          Retry
        </Button>
      </p>
    );
  }

  return (
    <section aria-label="Clinical team setup" className="space-y-4 rounded-3xl border p-4">
      {!practicesOnly && (
        <TeamAccessEditor teamId={team.id} request={request} tenantAdmin sendByDefault={false} />
      )}
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Outpatient practices</h3>
        {active && (
          <Button size="sm" variant="outline" onClick={() => setShowPractice(!showPractice)}>
            {showPractice ? 'Cancel' : 'Add outpatient practice'}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Add referring practices for this team. Registered team members can view these records in
        Clinical Trials. Adding a practice sends no email and grants no access to Rudi.
      </p>
      {practices.isLoading && <p className="text-sm">Loading practices…</p>}
      {practices.error && (
        <p role="alert" className="text-sm">
          Could not load practices.
          <Button variant="ghost" size="sm" onClick={() => practices.refetch()}>
            Retry practices
          </Button>
        </p>
      )}
      {practices.data?.length === 0 && <p className="text-sm">No outpatient practices yet.</p>}
      {practices.data?.map(row => (
        <div key={row.id} className="rounded-3xl border p-3 text-sm">
          <p className="font-medium">{row.name}</p>
          <p>{[row.address, row.postalCode, row.city].filter(Boolean).join(', ')}</p>
          <p>{[row.contactName, row.contactEmail, row.contactPhone].filter(Boolean).join(' · ')}</p>
        </div>
      ))}
      {showPractice && (
        <form
          aria-label="Add outpatient practice"
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={async event => {
            event.preventDefault();
            try {
              await createPractice.mutateAsync(practice);
              setPractice(emptyPractice);
              setShowPractice(false);
              toast.success('Outpatient practice added to team');
            } catch (error) {
              toast.error(detail(error));
            }
          }}
        >
          {practiceFields.map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`practice-${team.id}-${key}`}>{label}</Label>
              <Input
                id={`practice-${team.id}-${key}`}
                required={key === 'name'}
                maxLength={key === 'postalCode' ? 32 : key === 'address' ? 2000 : 255}
                type={key === 'contactEmail' ? 'email' : 'text'}
                value={practice[key]}
                onChange={event => setPractice({ ...practice, [key]: event.target.value })}
              />
            </div>
          ))}
          <Button
            type="submit"
            size="sm"
            disabled={!practice.name.trim() || createPractice.isPending}
          >
            Save practice
          </Button>
        </form>
      )}
    </section>
  );
}
